import { useMemo } from "react";
import { prepareUnifiedDataPostRequestBody } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import { useFetch } from "@/hooks/FetchHook";
import type { TableState, TableStateFunctions } from "@/types/tableState.types";
import type { TableMetadataType } from "@/types/tableMetadata";
import { isAbsoluteUrl } from "@/utils/apiUrl";
import { BASE_ENDPOINT } from "@/components/Templates/constants";
import { buildDashboardRequestFilters } from "./dashboardDataUtils";

export type DashboardDataResponse = {
  results: any[];
  count: number;
};

export const useDashboardData = ({
  currentTableState,
  datasetName,
  endpointPrefix,
  metadata,
  subjectId,
  tableStateFunctions,
  fixedFilters,
  excludeFilterKeys,
  unfilteredCountEnabled = false,
}: {
  currentTableState: TableState;
  datasetName: string;
  endpointPrefix?: string;
  metadata: TableMetadataType;
  subjectId?: string;
  tableStateFunctions: TableStateFunctions;
  fixedFilters?: TableState["filters"];
  excludeFilterKeys?: string[];
  unfilteredCountEnabled?: boolean;
}) => {
  const globalFiltersForTable = useMemo(
    () =>
      buildDashboardRequestFilters({
        currentFilters: currentTableState.filters,
        subjectId,
        fixedFilters,
        excludedFilterKeys: excludeFilterKeys,
      }),
    [currentTableState.filters, excludeFilterKeys, fixedFilters, subjectId],
  );

  const dataEndpointSuffix = isAbsoluteUrl(endpointPrefix)
    ? `${BASE_ENDPOINT}?dataset_type=${encodeURIComponent(datasetName)}`
    : `${BASE_ENDPOINT}/?dataset_type=${encodeURIComponent(datasetName)}`;
  const { current: currentPage, pageSize } = currentTableState.pagination;
  const payload = useMemo(
    () => ({
      dataset_type: datasetName,
      ...prepareUnifiedDataPostRequestBody({
        page: currentPage,
        pageSize,
        global_filters: globalFiltersForTable,
        allFiltersData: metadata.filters,
        sorting: currentTableState.sorting,
      }),
    }),
    [
      currentPage,
      pageSize,
      currentTableState.sorting,
      datasetName,
      globalFiltersForTable,
      metadata.filters,
    ],
  );

  const dataQuery = useFetch({
    endpointPrefix,
    endpointSuffix: dataEndpointSuffix,
    method: "POST",
    body: payload,
    useQueryOptions: {
      placeholderData: (previousData) => previousData,
    },
    callback: (data: DashboardDataResponse) => {
      if (currentTableState.pagination.total !== data.count) {
        tableStateFunctions.setPagination({
          ...currentTableState.pagination,
          total: data.count,
        });
      }
    },
    extraQueryKey: subjectId,
  });

  const unfilteredCountPayload = useMemo(() => {
    if (!unfilteredCountEnabled) return undefined;

    const globalFilters = buildDashboardRequestFilters({
      currentFilters: {},
      subjectId,
      fixedFilters,
    });

    return {
      dataset_type: datasetName,
      ...prepareUnifiedDataPostRequestBody({
        page: 1,
        pageSize: 1,
        global_filters: globalFilters,
        allFiltersData: metadata.filters,
      }),
    };
  }, [
    datasetName,
    fixedFilters,
    metadata.filters,
    subjectId,
    unfilteredCountEnabled,
  ]);

  const unfilteredCountQuery = useFetch({
    endpointPrefix,
    endpointSuffix: dataEndpointSuffix,
    method: "POST",
    body: unfilteredCountPayload,
    enabled: unfilteredCountEnabled,
    extraQueryKey: `${subjectId || ""}-unfiltered-count`,
  });

  return {
    data: dataQuery.data as DashboardDataResponse | undefined,
    dataEndpointSuffix,
    loadingData: dataQuery.isLoading,
    unfilteredCount: (
      unfilteredCountQuery.data as DashboardDataResponse | undefined
    )?.count,
    unfilteredCountLoading: unfilteredCountQuery.isLoading,
    refreshingData: dataQuery.isFetching && !dataQuery.isLoading,
  };
};
